import { Accordion, ActionIcon, Button, CloseButton, Popover, Text, TextInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconGpsFilled, IconHelp, IconSearch, IconSparkles } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { fastAutoHealAtom } from '~/atoms/recorder-atoms';
import { getTraceUrl } from '~/common/trace/getTraceUrl';
import { MonacoInputField } from '~/components/forms/input/MonacoInputField';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { api } from '~/trpc-extra/api';
import type { TargetEntry } from '../converters';
import clsx from 'clsx';

export default function TargetPart({
  testContentId,
  operationId,
  target,
  onClick,
  toggleEditing,
  readonly,
  updateFunc,
  scrollLevel,
}: {
  testContentId: string;
  operationId: string;
  target: TargetEntry;
  onClick?: () => void;
  toggleEditing: (editing: boolean) => void;
  readonly?: boolean;
  updateFunc: (target: TargetEntry) => Promise<void>;
  scrollLevel?: 'page' | 'element';
}) {
  const testVariables = useTestVariables({ testContentId, limit: { type: 'operationId', id: operationId } });

  const { isAdmin } = useAuthData();
  const [editing, setEditing] = useState(false);

  const fastAutoHealedOperations = useAtomValue(fastAutoHealAtom);
  const wasFastAutoHealed = fastAutoHealedOperations.some((item) => item.operationId === operationId);

  const { data: traceZipUrl } = api.operation.getTraceUrl.useQuery({ operationId }, { enabled: isAdmin });

  const form = useForm<TargetEntry>({
    initialValues: {
      name: target?.name || '',
      locator: target?.locator || '',
    },
    validate:
      scrollLevel ?
        {
          locator: (locator, values) => {
            const hasName = values.name?.trim();
            const hasLocator = locator?.trim();

            // For scroll operations: either both filled or both empty
            if ((hasName && !hasLocator) || (!hasName && hasLocator)) {
              return 'Both fields must be filled for element-level scroll, or both empty for page-level scroll';
            }
          },
        }
      : undefined,
  });
  useEffect(() => form.setValues(target), [target]);

  return (
    <Popover
      width={365}
      position="bottom"
      withArrow
      shadow="md"
      opened={editing}
      closeOnClickOutside
      onDismiss={() => {
        toggleEditing(false);
        setEditing(false);
      }}
      onClose={() => {
        toggleEditing(false);
        setEditing(false);
      }}
    >
      <Popover.Target>
        <div
          className={clsx(
            'flex min-w-0 flex-1 flex-nowrap items-center gap-1 break-words rounded-md hover:cursor-pointer',
            wasFastAutoHealed ? 'text-purple-500' : 'text-teal-800 dark:text-teal-600',
          )}
          onDoubleClick={() => {
            onClick?.();
            toggleEditing(true);
            setEditing(true);
          }}
        >
          {!!wasFastAutoHealed && (
            <IconSparkles
              size={14}
              className="flex-shrink-0 text-purple-500"
            />
          )}
          <span className="break-all">{target.name || (scrollLevel === 'page' ? 'the page' : 'an element')}</span>
        </div>
      </Popover.Target>
      <Popover.Dropdown
        className="dark:bg-[#242424]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col pb-4">
          <div className="flex justify-end">
            <div className="flex gap-2">
              {isAdmin && traceZipUrl ?
                <ActionIcon
                  title="[Admin] Trace"
                  component={Link}
                  href={getTraceUrl({ traceZipUrl })}
                  rel="noopener noreferrer"
                  target="_blank"
                  color="orange"
                  variant="transparent"
                >
                  <IconGpsFilled />
                </ActionIcon>
              : null}
              <CloseButton
                variant="transparent"
                size="md"
                onClick={() => {
                  toggleEditing(false);
                  setEditing(false);
                }}
              />
            </div>
          </div>
          <form
            className="flex flex-col gap-2"
            onSubmit={form.onSubmit(async (values) => {
              toggleEditing(!editing);
              setEditing(!editing);
              await updateFunc(values);
            })}
          >
            <TextInput
              label={
                <div className="flex items-center gap-1">
                  <span>Element name</span>
                  <Tooltip
                    label="This element name is automatically generated by AI and is used during auto-heal to infer the original intention. You can modify it to better describe the element."
                    multiline
                    w={280}
                    withArrow
                  >
                    <IconHelp
                      size={16}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </Tooltip>
                </div>
              }
              disabled={readonly}
              {...form.getInputProps('name')}
            />
            <MonacoInputField
              autoFocus={!readonly}
              label="Element locator"
              disabled={readonly}
              readonlyMessage="Read-only. Edit test to modify"
              variables={testVariables}
              {...form.getInputProps('locator')}
            />

            {!readonly && (
              <>
                <div className="flex justify-end space-x-1 py-4">
                  <Button
                    type="submit"
                    disabled={readonly}
                  >
                    Save
                  </Button>
                </div>
                <LocatorHelp className="mt-2 dark:bg-neutral-800" />
              </>
            )}
          </form>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
function LocatorHelp({ className }: { className?: string }) {
  return (
    <Accordion
      chevronPosition="right"
      variant="contained"
      className={className}
    >
      <Accordion.Item
        value="locator-help"
        key="locator-help"
      >
        <Accordion.Control>
          <div className="flex flex-nowrap items-center gap-2">
            <IconSearch size={18} />
            <span className="text-blue-700 dark:text-blue-300">Still not finding a stable locator?</span>
          </div>
        </Accordion.Control>
        <Accordion.Panel>
          <Text size="sm">
            Your developer can add a{' '}
            <code className="rounded bg-red-50 px-1.5 py-0.5 font-mono text-sm text-red-600 dark:bg-red-950/50 dark:text-red-400">
              data-testid
            </code>{' '}
            attribute to the element to make it easier to locate.
          </Text>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
